<template>
  <b-container fluid style="height:100%">
    <b-row v-if="showSearch">
      <b-col cols="11">
        <b-form-group
          description="You can use * and ? as wildcards"
          class="mb-4"
        >
          <b-form-input
            type="search"
            placeholder="Search documents"
            @keyup="debounceSearch"
          ></b-form-input>
        </b-form-group>
      </b-col>
      <b-col cols="1">
        <b-btn variant="primary" @click="closeSearch">
          <i class="fa fa-times" />
        </b-btn>
      </b-col>
    </b-row>

    <b-row style="height:100%">
      <b-col class="focus-doctable">
        <b-table
          :items="focusDocuments"
          :fields="enabledFields"
          no-local-sorting
          small
          class="small"
          @sort-changed="sortingChanged"
        >
          <template v-slot:head(actions)>
            <b-btn variant="link-dark" @click="showSearch = !showSearch">
              <i class="fa fa-search" />
            </b-btn>
            <b-dropdown variant="link-dark" no-caret dropright>
              <template slot="button-content"
                ><i class="fa fa-ellipsis-v"
              /></template>

              <b-dropdown-form>
                <b-form-group label="Show columns">
                  <b-form-checkbox v-model="fieldOptions.pubdate" class="mb-1"
                    >Date</b-form-checkbox
                  >
                  <b-form-checkbox v-model="fieldOptions.author" class="mb-1"
                    >Author</b-form-checkbox
                  >
                  <b-form-checkbox v-model="fieldOptions.title" class="mb-1"
                    >Title</b-form-checkbox
                  >
                  <b-form-checkbox v-model="fieldOptions.citations" class="mb-1"
                    >CT</b-form-checkbox
                  >
                  <b-form-checkbox
                    v-model="fieldOptions.familycitations"
                    class="mb-1"
                    >FC</b-form-checkbox
                  >
                </b-form-group>
              </b-dropdown-form>
            </b-dropdown>
          </template>

          <template v-slot:cell(pubdate)="data" class="w-10">
            <div>
              {{ data.item.pubdate }}
            </div>
          </template>
          <template v-slot:cell(author)="data" class="w-20">
            <div>
              {{ data.item.author }}
            </div>
          </template>
          <template v-slot:cell(title)="data" class="w-30">
            <div>
              <b-link
                class="text-dark fix-overflow"
                @click="viewDocument(data.item.webkey)"
              >
                {{ data.item.title }}
              </b-link>
            </div>
          </template>
          <template v-slot:head(citations) class="w-10">
            <span v-b-popover.hover.top="'Citations'">CT</span>
          </template>
          <template v-slot:head(familycitations) class="w-10">
            <span v-b-popover.hover.top="'Family citations'">FC</span>
          </template>
          <template v-slot:cell(actions)="data" class="w-10">
            <div class="actions">
              <b-btn
                v-if="isFavorite(data.item.webkey)"
                variant="link-dark"
                size="sm"
                class="py-0 px-1"
                @click="unfavoriteDocument(data.item.webkey)"
              >
                <i class="fa fa-star" />
              </b-btn>
              <b-btn
                v-else
                variant="link-dark"
                size="sm"
                class="py-0 px-1"
                @click="favoriteDocument(data.item.webkey)"
              >
                <i class="far fa-star" />
              </b-btn>
              <b-btn
                variant="link-dark"
                size="sm"
                class="py-0 px-1"
                @click="viewDocument(data.item.webkey)"
              >
                <i class="fa fa-eye" />
              </b-btn>
              <b-btn
                variant="link-dark"
                size="sm"
                class="py-0 px-1"
                :href="typeof data.item.url === 'object' ? '' : data.item.url"
                target="_blank"
              >
                <i class="fa fa-external-link-alt" />
              </b-btn>
            </div>
          </template>
        </b-table>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import _ from "lodash";
import { mapActions, mapState } from "vuex";

export default {
  name: "DocumentsTableFocus",
  data() {
    return {
      fields: [
        {
          key: "pubdate",
          label: "Date",
          sortable: true,
          thClass: "w-10",
          tdClass: "w-10"
        },
        {
          key: "author",
          label: "Author",
          sortable: true,
          thClass: "w-20",
          tdClass: "w-20 fix-overflow"
        },
        {
          key: "title",
          label: "Title",
          sortable: true,
          thClass: "w-30",
          tdClass: "w-30 fix-overflow"
        },
        {
          key: "citations",
          label: "CT",
          sortable: true,
          thClass: "w-10 text-right",
          tdClass: "w-10 text-right"
        },
        {
          key: "familycitations",
          label: "FC",
          sortable: true,
          thClass: "w-10 text-right",
          tdClass: "w-10 text-right"
        },
        {
          key: "actions",
          label: "",
          sortable: false,
          thClass: "w-20 th-actions text-right",
          tdClass: "w-20 text-right"
        }
      ],
      fieldOptions: {
        pubdate: true,
        author: true,
        title: true,
        citations: true,
        familycitations: true
      },
      sortOn: "pubdate",
      sortAsc: false,
      currentPage: 1,
      perPage: 1000,
      perPageOptions: [
        { value: 10, text: "10 per page" },
        { value: 15, text: "15 per page" },
        { value: 30, text: "30 per page" }
      ],
      jumpToPageInput: null,
      filter: null,
      showSearch: false
    };
  },
  computed: {
    ...mapState({
      apiId: state => state.api.apiId,
      documentsCount: state => state.document.documentsCount,
      favorites: state => state.favorite.favorites,
      focusDocuments: state => state.document.focusDocuments,
      subsetFilter: state => state.filter.subsetFilter
    }),
    totalPages() {
      return Math.floor(
        this.documentsCount / this.perPage +
          (this.documentsCount % this.perPage) >
          0
          ? 1
          : 0
      );
    },
    totalRows() {
      return (
        Math.floor(this.documentsCount / this.perPage) * this.perPage +
        (this.documentsCount % this.perPage)
      );
    },
    enabledFields() {
      let enabledFields = [];
      _.each(this.fieldOptions, (v, k) => {
        if (v === true) {
          enabledFields.push(this.fields.find(f => f.key === k));
        }
      });
      enabledFields.push(this.fields.find(f => f.key === "actions"));
      return enabledFields;
    }
  },
  watch: {
    subsetFilter() {
      this.loadDocumentsPage();
    }
  },
  mounted() {
    this.loadDocumentsPage();
  },
  methods: {
    ...mapActions([
      "loadFocusDocuments",
      "loadFocusDocSelection",
      "saveFavorite",
      "deleteFavorite"
    ]),
    isFavorite(webkey) {
      return (
        this.favorites &&
        this.favorites.length &&
        this.favorites.find(f => f === webkey)
      );
    },
    favoriteDocument(webkey) {
      this.saveFavorite({ webkey: webkey });
    },
    unfavoriteDocument(webkey) {
      this.deleteFavorite({ webkey: webkey });
    },
    viewDocument(webkey) {
      this.$emit("viewDocument", webkey);
    },
    loadDocumentsPage() {
      this.loadFocusDocSelection({ selection: this.subsetFilter });
      this.loadFocusDocuments({
        which: "global",
        page: 1,
        pageSize: 1000,
        sortOn: this.sortOn,
        sortAsc: this.sortAsc,
        search: this.filter
      });
      this.currentPage = 1;
    },
    debounceSearch: _.debounce(function(e) {
      this.filter = e.target.value.trim();
      this.loadDocumentsPage(1);
    }, 400),
    closeSearch() {
      this.showSearch = false;
      this.filter = null;
      this.loadDocumentsPage(1);
    },
    sortingChanged(e) {
      if (this.sortOn === e.sortBy) {
        this.sortAsc = !e.sortDesc;
      } else {
        this.sortOn = e.sortBy;
        this.sortAsc = true;
      }
      this.loadDocumentsPage(1);
    },
    jumpToPage() {
      if (
        isNaN(this.jumpToPageInput) ||
        this.jumpToPageInput > this.totalPages
      ) {
        return false;
      }

      this.loadDocumentsPage(this.jumpToPageInput);
      this.jumpToPageInput = null;
    }
  }
};
</script>
